import TimeFacet from './TimeFacetPresentation';
import moment, { Moment } from 'moment';
import { FacetLogic, RenderFacetProps } from './Types';
import { Field } from '../../../types/Field';
import { FacetValueCount, FacetValues, ReplayAPI_TimeStartFacetValue, TimeStartFacetValue } from '../../../types/Facet';


// Do not change the order of the timePeriodDefinitions, because the index is used to determine the periodIndex of the TimeStartFacetValue.
// Would be nice to refactor this, because it would make the code more readable.
export const timePeriodDefinitions = [
    { displayName: "Anytime", period: moment.duration(100, 'years'), default: true, showAlways: true},
    { displayName: "Last year", period: moment.duration(1, 'years'), default: false, showAlways: true},
    { displayName: "Last month", period: moment.duration(1, 'months'), default: false, showAlways: true},
    { displayName: "Last week", period: moment.duration(1, 'weeks'), default: false, showAlways: false},
    { displayName: "Last 3 days", period: moment.duration(3, 'days'), default: false, showAlways: false},
    { displayName: "Last day", period: moment.duration(1, 'days'), default: false, showAlways: false},
    { displayName: "Last 3 hours", period: moment.duration(3, 'hours'), default: false, showAlways: false},
    { displayName: "Last hour", period: moment.duration(1, 'hours'), default: false, showAlways: false},
]

export function getDefaultTimePeriodIndex() {
    let defaultIndex = 0;

    timePeriodDefinitions.forEach((item, index) => {
        if (item.default) {
            defaultIndex = index;
        }
    });
    return defaultIndex;
}

function getFromDateTimeValue (facetValue: TimeStartFacetValue | null) : Moment {
    if (facetValue && facetValue.fromDateTime) {
        return facetValue.fromDateTime;
    }
    return moment().subtract(timePeriodDefinitions[getDefaultTimePeriodIndex()].period);
}

function getToDateTimeValue (facetValue: TimeStartFacetValue | null) : Moment {
    if (facetValue && facetValue.toDateTime) {
        return facetValue.toDateTime;
    }
    return moment();
}

function getselectedIndex (facetSelection: FacetValues<TimeStartFacetValue> ) {
    if (facetSelection && facetSelection.facetValues.length > 0) {
        return facetSelection.facetValues[0].periodIndex;
    }

    return getDefaultTimePeriodIndex();
}

function toPeriodEntries(fromDateTime: any, toDateTime: any, checkedIndex: any, facetValues: FacetValueCount<TimeStartFacetValue>[]) {
    return facetValues.map((facetValue: FacetValueCount<TimeStartFacetValue>) => {
        if (facetValue.facetValue.periodIndex === null || facetValue.facetValue.periodIndex === undefined) {
                return null;
        }
        
        let displayCount = false;
        let facetFromDateTime = facetValue.facetValue.periodIndex === null ? facetValue.facetValue.fromDateTime : moment().subtract(timePeriodDefinitions[facetValue.facetValue.periodIndex].period);
        let facetToDateTime = moment().subtract(moment.duration(5, 'minutes'));
        if (fromDateTime > facetFromDateTime! || toDateTime < facetToDateTime!) {
            displayCount = false;
        } else {            
            displayCount = true;
        }

        return {
            displayName: timePeriodDefinitions[facetValue.facetValue.periodIndex].displayName,
            periodIndex: facetValue.facetValue.periodIndex,
            period: timePeriodDefinitions[facetValue.facetValue.periodIndex].period,
            checked: facetValue.facetValue.periodIndex === checkedIndex,
            showAlways: timePeriodDefinitions[facetValue.facetValue.periodIndex].showAlways,
            displaycount: displayCount,
            count: facetValue.count,
        };
    });
}

function getDefaultSelection(fieldName: string) : FacetValues<TimeStartFacetValue> {
    return {
        facetName: fieldName,        
        facetValues: [            
            new TimeStartFacetValue(
                fieldName,
                getDefaultTimePeriodIndex(),
                timePeriodDefinitions[getDefaultTimePeriodIndex()].period,  
                moment().subtract(timePeriodDefinitions[getDefaultTimePeriodIndex()].period),
                moment()                
            )  
        ]                   
    };
}

function createFilter(facetSelection: FacetValues<TimeStartFacetValue>, field: Field) {
    if (facetSelection.facetValues.length === 0) {
        console.warn("No facet values selected for time facet. Which should not happen.", facetSelection);
        return "";
    }
    
    const fromDateTime  = facetSelection.facetValues[0].fromDateTime;
    const toDateTime  = facetSelection.facetValues[0].toDateTime;
    const selectedIndex = facetSelection.facetValues[0].periodIndex;

    if (!fromDateTime || !toDateTime) {
        return "";
    }

    const fromFilter = field.systemSettings.filterClause.replace('{{VALUE}}', fromDateTime.format()).replace('{{OPERATOR}}', 'gt')
    if (selectedIndex !== null) {
        return `(${fromFilter})`; 
    }

    const toFilter = field.systemSettings.filterClause.replace('{{VALUE}}', toDateTime.format()).replace('{{OPERATOR}}', 'lt')
    return `(${fromFilter} and ${toFilter})`;    
}

function createRequest(field: Field) {
    let values = "";

    for (let i = 0; i < timePeriodDefinitions.length; i++) {
        if (values !== "") {
            values = values + "|";
        }
        values = values + moment().subtract(timePeriodDefinitions[i].period).utc().format();
    }

    if (values.length > 0) {
        return field.name + ",values:" + values
    }

    return field.name;
}

function extractFacetValues(facetName: string, facetValuesFromSearch: ReplayAPI_TimeStartFacetValue[]) : FacetValueCount<TimeStartFacetValue>[] {    
    let periodValues: FacetValueCount<TimeStartFacetValue>[] = [];
    // Skip the first facet result (is count till last year)
    for (let i = 1, facetIndex = 0; i < facetValuesFromSearch.length; i++, facetIndex++) {
        periodValues.push({
            facetValue: new TimeStartFacetValue(
                facetName,
                facetIndex,
                timePeriodDefinitions[facetIndex].period,
                null,
                null,
            ),            
            count: facetValuesFromSearch[i].count,            
        })

        // Add count to all previous periods because they overlap 
        for (let j = 0; j < facetIndex; j++) {
            periodValues[j].count = periodValues[j].count + facetValuesFromSearch[i].count;
        }
    }

    return periodValues.reverse();
}

function renderFacet (props: RenderFacetProps) {
    let facetSelection = props.facetSelection ? props.facetSelection as FacetValues<TimeStartFacetValue> : getDefaultSelection(props.field.name);

    const fromDateTime = getFromDateTimeValue(facetSelection.facetValues.length > 0 ? facetSelection.facetValues[0] : null);
    const toDateTime = getToDateTimeValue(facetSelection.facetValues.length > 0 ? facetSelection.facetValues[0] : null);
    const selectedIndex = getselectedIndex(facetSelection);

    let periodEntries = toPeriodEntries(fromDateTime, toDateTime, selectedIndex, props.facetValues as FacetValueCount<TimeStartFacetValue>[]);

    return <TimeFacet 
      key={props.field.name}
      name={props.field.name} 
      displayName={props.field.userSettings.displayName}
      periodData={periodEntries} 
      fromDateTime={fromDateTime}
      toDateTime={toDateTime}
      onChangeFacet={props.onChangeFacetSelection}
      isExecutingSearch={props.isExecutingSearch}
    />
}

// The facetValue contains an object with the new from and to date-time. 
// The returned selection of this facet type is not based upon the previous selections, 
// the received value is simply returned.
function createUpdatedSelection(facetValue: TimeStartFacetValue, prevFacetSelection: FacetValues<TimeStartFacetValue>): FacetValues<TimeStartFacetValue> {        
    return {
        facetName: prevFacetSelection.facetName,
        facetValues: [facetValue],
    };
}

export const timeFacetLogic : FacetLogic = {
    dataType: 'Edm.DateTimeOffset',
    getDefaultSelection: getDefaultSelection,
    createFilter: createFilter,
    createRequest: createRequest,
    extractFacetValues: extractFacetValues,
    renderFacet: renderFacet,
    createUpdatedSelection: createUpdatedSelection,
}